import {
  ApiEingangslieferscheinSubmitData,
  ApiEingangslieferscheinSubmitPosition,
  submitLieferschein,
} from '@/api/module/incoming';
import {
  ApiInventoryDeleteDatum,
  deleteInventory,
  getDetailInventory,
  patchInventory,
  postInventory,
} from '@/api/module/inventory';
import {
  ApiSuggestionSubmitData,
  ApiSuggestionSubmitPosition,
  deleteSuggestionPosition,
  getDetailSuggestion,
  postSuggestionPosition,
  putSuggestionPosition,
  submitSuggestion,
} from '@/api/module/order';
import {ApiPicklistSubmitData, cancelPicklist, createPicklist, editPicklist, parkPicklist, submitPicklist} from '@/api/module/picklist';
import {ApiArticle} from '@/model/Article';
import {ApiArticleEANUnitType} from '@/model/ArticleEAN';
import {ApiBooking} from '@/model/Booking';
import {ApiBookingWarehouse} from '@/model/BookingWarehouse';
import Inventory from '@/model/Inventory';
import {ApiInventoryPositionReduced} from '@/model/InventoryPosition';
import {ApiNVE} from '@/model/NVE';
import Picklist, {ApiPicklistState, ApiPicklistType} from '@/model/Picklist';
import Position, {ApiPosition} from '@/model/Position';
import {ApiSuggestionPosition} from '@/model/SuggestionPosition';
import {autoImplement, doAlert, doConfirm} from '@/util';
import EventEmitter from '@/util/EventEmitter';
import {defineStore} from 'pinia';
import {useSettings} from './settings';
import moment, {Moment} from 'moment';
import {NativeAudio} from '@capgo/native-audio';
import useDetailPicklistStore from './detailPicklist';
import usePicklistsStore from './picklists';
import useInventoriesStore from './inventories';
import useSuggestionsStore from './suggestions';
import useOrdersStore from './orders';
import {
  ApiUmbuchungSubmitData,
  ApiUmbuchungSubmitPosition,
  deleteUmbuchungPosition, getUmbuchungPositionen,
  postUmbuchungPosition,
  putUmbuchungPosition, submitUmbuchung,
} from '@/api/module/umbuchung';
import {ApiUmbuchungPosition} from '@/model/UmbuchungPosition';
import useUmbuchungsStore from '@/store/umbuchung';
import {Dialog} from '@capacitor/dialog';
import {Device} from '@capacitor/device';
import useSubsidiariesStore from '@/store/subsidiaries';
import useCorrectionsStore from '@/store/corrections';
import {
  ApiBestandskorrekturSubmitData, ApiBestandskorrekturSubmitPosition,
  deleteCorrectionPosition, getCorrectionPositionen,
  postCorrectionPosition,
  putCorrectionPosition, submitCorrection,
} from '@/api/module/correction';
import {ApiCorrectionPosition} from '@/model/Correction';

export const INT_MAX = 2147483647;

export class NotFoundError extends Error {
}

export class InvalidInputError extends Error {
}

export class UserAbortError extends Error {
}

export interface PicklistPickerBooking {
  qty: number;
  batchNo: number | null;
  serialNo: string | null;
  nve: string | null;
  warehouse: number | null;
  subsidiary: number | null;
  shelf: number | null;

  // Wareneingang
  generateNve: boolean;
  acknowledgementNumber: string | null;
  dealerBatchNo: string | null;
  bestBeforeDate: string | null;
  manufacturingDate: string | null;

  // Umbuchung
  destinationWarehouse?: number;
  destinationSubsidiary?: number | null;
  destinationShelf?: number | null;
}

export interface BookingInterface extends PicklistPickerBooking {
  position: Position;
  picklistType: ApiPicklistType;
}

export class Booking extends autoImplement<BookingInterface>() {
  get qtyGebinde(): number | null {
    return this.position.Gebinhalt ? this.qty / this.position.Gebinhalt : null;
  }

  get qtyPaletten(): number | null {
    return this.position.mengeProPalette ? this.qty / this.position.mengeProPalette : null;
  }

  get batchNoValid() {
    return (
      !this.position.ChargenpflichtBool ||
      // eslint-disable-next-line max-len
      (this.batchNo !== null && this.batchNo > 0 && !(this.picklistType === ApiPicklistType.Umbuchung || this.picklistType === ApiPicklistType.Correction)) ||
      // eslint-disable-next-line max-len
      (this.position.Chargen.find((ch) => ch.Chargennr === this.batchNo) && (this.picklistType === ApiPicklistType.Umbuchung || this.picklistType === ApiPicklistType.Correction)) ||
      this.picklistType === ApiPicklistType.Incoming ||
      this.picklistType === ApiPicklistType.Suggestion
    );
  }

  get serialNoValid() {
    return !this.position.SeriennrpflichtBool || this.serialNo !== null || this.picklistType === ApiPicklistType.Suggestion;
  }

  get nveValid() {
    return (this.nve?.toString().length ?? 0) <= 18;
  }

  get valid() {
    if (!this.batchNoValid) return false;
    if (!this.serialNoValid) return false;
    if (!this.nveValid) return false;
    return true;
  }

  get isEmpty() {
    return this.batchNo === null && this.nve === null && this.qty === 0 && this.serialNo === null;
  }
}

interface PicklistPickerPickData {
  position: Position;
  bookings: Booking[];
}

async function updateStore(type: ApiPicklistType): Promise<void> {
  switch (type) {
    case ApiPicklistType.Order:
    case ApiPicklistType.Tour:
      const pickListStore = usePicklistsStore();
      return await pickListStore.fetch();
    case ApiPicklistType.Inventory:
      const inventoriesStore = useInventoriesStore();
      return await inventoriesStore.fetch();
    case ApiPicklistType.Suggestion:
      const suggestionsStore = useSuggestionsStore();
      return await suggestionsStore.fetch();
    case ApiPicklistType.Incoming:
      const ordersStore = useOrdersStore();
      return await ordersStore.fetch();
    case ApiPicklistType.Umbuchung:
      const umbuchungStore = useUmbuchungsStore();
      return await umbuchungStore.fetch();
    case ApiPicklistType.Correction:
      const correctionStore = useCorrectionsStore();
      return await correctionStore.fetch();
    case ApiPicklistType.Internal:
      return;
  }
  throw new Error('not exhausted');
}

export class PickData extends autoImplement<PicklistPickerPickData>() {
  get qty() {
    return this.bookings.reduce((cum, p) => cum + p.qty, 0);
  }

  get qtyGebinde() {
    return this.position.Gebinhalt ? this.qty / this.position.Gebinhalt : null;
  }

  get qtyPaletten() {
    return this.position.mengeProPalette ? this.qty / this.position.mengeProPalette : null;
  }

  get qtyLeft() {
    return this.position.Menge - this.qty;
  }

  get qtyGebindeLeft() {
    return typeof this.qtyGebinde === 'number' && typeof this.position.Gebindemenge === 'number' ? this.position.Gebindemenge - this.qtyGebinde : null;
  }

  get qtyPalettenLeft() {
    return typeof this.qtyPaletten === 'number' && typeof this.position.Palettenmenge === 'number' ? this.position.Palettenmenge - this.qtyPaletten : null;
  }

  get full() {
    return this.qtyLeft <= 0 && this.position.Menge > 0;
  }

  get valid() {
    return !this.bookings.some((b) => !b.valid && (b.picklistType === ApiPicklistType.Inventory || b.qty > 0));
  }

  get done() {
    return this.full && this.valid;
  }

  get overbooked() {
    return this.qtyLeft < 0 && this.position.Menge > 0;
  }
}

interface PicklistPickerState {
  picked: Record<string, PickData>;
  overrideReadonly: boolean | null;
  canceling: boolean;
  editing: boolean;
  parking: boolean;
  submitting: boolean;
  selectedPosition: number | null;
  eventEmitter: EventEmitter;
  editError: Error | null;
}

interface ModifyOptions extends Partial<PicklistPickerBooking> {
  position: Position,
  addQty?: number;
  bookingIndex?: number;
}

export interface ModifyByScanOptions extends Omit<ModifyOptions, 'position'> {
  ean?: string;
  insertInCurrentPositionIfNotFound?: boolean;
}

type SuggestionSubmitData = {
  type: 'create';
  create: ApiSuggestionSubmitData;
} | {
  type: 'modify';
  added: ApiSuggestionSubmitPosition[];
  modified: ApiSuggestionSubmitPosition[];
  deleted: number[];
}

type UmbuchungSubmitData = {
  type: 'create';
  create: ApiUmbuchungSubmitData;
} | {
  type: 'modify';
  added: ApiUmbuchungSubmitPosition[];
  modified: ApiUmbuchungSubmitPosition[];
  deleted: number[];
}

type CorrectionSubmitData = {
  type: 'create';
  create: ApiBestandskorrekturSubmitData;
} | {
  type: 'modify';
  added: ApiBestandskorrekturSubmitPosition[];
  modified: ApiBestandskorrekturSubmitPosition[];
  deleted: number[];
}

export const usePicklistPickerStore = defineStore('picklistPicker', {
  state(): PicklistPickerState {
    return {
      picked: {} as Record<string, PickData>,
      overrideReadonly: null,
      canceling: false,
      editing: false,
      parking: false,
      submitting: false,
      selectedPosition: null,
      eventEmitter: new EventEmitter(),
      editError: null,
    };
  },
  actions: {
    reset() {
      if (!this.picklist) return;
      this.picked = {};

      this.picklist.allPositions.forEach((p) => {
        p.Buchungen.forEach((b, i) => {
          console.log('reset calles', b, p);
          const booking = this.getBooking(p, i, true);
          booking.qty = b.Menge;
          booking.nve = b.NVE ?? null;
          booking.serialNo = b.SerienNr ?? null;

          if (b.AbgehendesLager) {
            booking.batchNo = b.AbgehendesLager.Chargennr ?? null;
            booking.warehouse = b.AbgehendesLager.Lagerhalle;
            booking.subsidiary = this.picklist?.NiederlassungAbgehend?.Niederlassung ?? b.AbgehendesLager.Niederlassung;
            booking.shelf = b.AbgehendesLager.Lagerfach ?? null;
          }

          if (b.EingehendesLager) {
            booking.destinationWarehouse = b.EingehendesLager.Lagerhalle;
            booking.destinationSubsidiary = this.picklist!.NiederlassungEingehend!.Niederlassung;
            booking.destinationShelf = b.EingehendesLager.Lagerfach ?? null;
          }
        });
      });

      // needed for reactivity
      this.picked = {...this.picked};
      this.selectedPosition = null;
    },
    // mark picklist as changed
    async commitChange(picklist: Picklist | null, type = picklist?.Art ?? null) {
      if (!type) {
        type = ApiPicklistType.Order;
        console.warn('type missing in commit');
      }

      const detailPicklistStore = useDetailPicklistStore();
      await detailPicklistStore.replace([picklist, undefined]);
      if (picklist) await updateStore(picklist.Art);
    },
    // prepare picklist for editing
    async edit(picklist?: Picklist) {
      console.log('start edit');
      this.editing = true;
      try {
        // Immer die vorgegebene Pickliste benutzen, wenn vorhanden
        const editedPicklist = this.picklist ?? picklist;
        if (!editedPicklist) throw new Error('Keine zu bearbeitende Pickliste gegeben');
        const newPicklist = editedPicklist;
        console.log('Picklist Status: ', editedPicklist.Status);
        // Readonly überschreiben, wenn Bearbeitung für Pickliste in Bearbeitung angefordert
        this.overrideReadonly = null;
        this.editError = null;
        if (this.derivedReadonly && editedPicklist.Status === ApiPicklistState.Active && await doConfirm('Diese Pickliste befindet sich in Bearbeitung. Wollen Sie die Bearbeitung übernehmen?', 'Bereits in Bearbeitung')) {
          this.overrideReadonly = false;
        }
        if (editedPicklist.Status === ApiPicklistState.Todo || editedPicklist.Status === ApiPicklistState.Parked) {
          // bearbeitbar
          if (editedPicklist.isOutgoing) {
            // per API zur Bearbeitung markieren
            try {
              if (picklist === undefined) {
                const editResult: Picklist | null = (await editPicklist(editedPicklist))[0];
                if (editResult != null) {
                  newPicklist.Status = editResult.Status;
                }
              } else {
                newPicklist.Status = ApiPicklistState.Todo;
              }

              this.overrideReadonly = false;
            } catch (err) {
              this.editError = err as Error;
              throw err;
            }
            if (this.derivedReadonly && newPicklist.Status === ApiPicklistState.Active) this.overrideReadonly = false;
            await this.commitChange(newPicklist);
          } else {
            // eingehende oder Inventuren auf aktiv setzen
            editedPicklist.Status = ApiPicklistState.Active;
            this.overrideReadonly = false;
            await this.commitChange(editedPicklist);
          }
        } else {
          await this.commitChange(editedPicklist);
        }

        // Werte aus Pickliste laden
        this.reset();
      } finally {
        this.editing = false;
      }
    },
    async cancel(picklist?: Picklist) {
      this.canceling = true;
      try {
        // Immer die vorgegebene Pickliste benutzen, wenn vorhanden
        const cancelledPicklist = this.picklist ?? picklist;
        if (!cancelledPicklist) throw new Error('Keine abzubrechende Pickliste gegeben');

        if (cancelledPicklist.isOutgoing) {
          // per API zum Abbrechen markieren
          const newPicklist = (await cancelPicklist(cancelledPicklist))[0];
          if (newPicklist != null) {
            cancelledPicklist.Status = newPicklist.Status;
            await this.commitChange(cancelledPicklist);
          }
        } else {
          // Status zurücksetzen
          cancelledPicklist.Status = ApiPicklistState.Todo;
        }

        this.overrideReadonly = null;
        // Werte zurücksetzen
        this.reset();
      } finally {
        this.canceling = false;
      }
    },
    async submit(picklist?: Picklist) {
      this.submitting = true;
      try {
        if (!this.picklist?.allPositions.length) throw new Error('Es sind keine Positionen vorhanden. Speichern nicht möglich!');
        if (!this.validate()) throw new Error('Es sind noch unvollständige Positionen vorhanden. Speichern nicht möglich!');
        // Immer die vorgegebene Pickliste benutzen, wenn vorhanden
        const submittedPicklist = this.picklist ?? picklist;
        if (!submittedPicklist) throw new Error('Keine abzubrechende Pickliste gegeben');
        switch (submittedPicklist.Art) {
          case ApiPicklistType.Incoming: {
            // Pickliste eingehend
            // Lieferschein abschicken;
            await submitLieferschein(submittedPicklist, this.getIncomingSubmitData());
            submittedPicklist.Status = ApiPicklistState.Todo;
            this.commitChange(submittedPicklist ?? null, ApiPicklistType.Incoming);
            this.reset();
            break;
          }
          case ApiPicklistType.Inventory: {
            await this.submitInventory(submittedPicklist);
            break;
          }
          case ApiPicklistType.Suggestion: {
            await this.submitSuggestion(submittedPicklist);
            break;
          }
          case ApiPicklistType.Umbuchung: {
            await this.submitUmbuchung(submittedPicklist);
            break;
          }
          case ApiPicklistType.Correction: {
            await this.submitCorrection(submittedPicklist);
            break;
          }
          default: {
            if (this.picklist.Picklistnr === '-1') {
              await createPicklist(this.getOutgoingCreateData());
            } else {
              await submitPicklist(submittedPicklist, this.getOutgoingSubmitData());
            }
            submittedPicklist.Status = ApiPicklistState.Done;
            await this.commitChange(submittedPicklist);
            break;
          }
        }
      } finally {
        this.submitting = false;
      }
    },
    async submitSuggestion(submittedPicklist: Picklist) {
      const submitData = await this.getSuggestionSubmitData();
      const errors: Error[] = [];

      if (submitData.type === 'create') {
        await submitSuggestion(submitData.create);
        submittedPicklist.Status = ApiPicklistState.Todo;
        this.commitChange(submittedPicklist ?? null, ApiPicklistType.Suggestion);
        this.reset();
        return;
      }

      if (submitData.added.length > 0) {
        try {
          await postSuggestionPosition(submittedPicklist.Picklistnr as number, {Positionen: submitData.added});
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (submitData.modified.length > 0) {
        try {
          await putSuggestionPosition(submittedPicklist.Picklistnr as number, {Positionen: submitData.modified});
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (submitData.deleted.length > 0) {
        try {
          await Promise.all(
              submitData.deleted.map((posNr) => deleteSuggestionPosition(submittedPicklist.Picklistnr as number, posNr)),
          );
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (errors.length > 0) {
        if (errors.length === 1) {
          throw errors[0];
        } else {
          throw new Error(`Mehrere Fehler\n${errors.join('\n')}`);
        }
      }

      submittedPicklist.Status = ApiPicklistState.Todo;
      this.commitChange(submittedPicklist ?? null, ApiPicklistType.Suggestion);
      this.reset();
    },
    async submitInventory(submittedPicklist: Picklist) {
      const submitData = await this.getInventorySubmitData();
      const errors: Error[] = [];
      if (submitData.modified.length) {
        try {
          await patchInventory({
            ...submitData.header,
            Positionen: submitData.modified,
          });
        } catch (err: any) {
          console.error(err);
          errors.push(err);
        }
      }
      if (submitData.added.length) {
        try {
          await postInventory({
            ...submitData.header,
            Positionen: submitData.added,
          });
        } catch (err: any) {
          console.error(err);
          errors.push(err);
        }
      }
      if (submitData.deleted.length) {
        try {
          await deleteInventory(submitData.deleted);
        } catch (err: any) {
          console.error(err);
          errors.push(err);
        }
      }

      if (errors.length > 0) {
        if (errors.length === 1) {
          throw errors[0];
        } else {
          throw new Error(`Mehrere Fehler\n${errors.join('\n')}`);
        }
      }

      submittedPicklist.Status = ApiPicklistState.Todo;
      this.commitChange(submittedPicklist ?? null, ApiPicklistType.Inventory);
      this.reset();
    },
    async submitUmbuchung(submittedPicklist: Picklist) {
      const submitData = await this.getUmbuchungSubmitData();
      const errors: Error[] = [];

      if (submitData.type === 'create') {
        await submitUmbuchung(submitData.create);
        submittedPicklist.Status = ApiPicklistState.Todo;
        this.commitChange(submittedPicklist ?? null, ApiPicklistType.Umbuchung);
        this.reset();
        return;
      }

      if (submitData.added.length > 0) {
        try {
          console.log('Create Pos: ', submitData.added);
          await postUmbuchungPosition(submittedPicklist.Picklistnr as number, {Positionen: submitData.added});
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (submitData.modified.length > 0) {
        try {
          await Promise.all(submitData.modified.map(async (item) => {
            console.log('Update Pos: ', item);
            await putUmbuchungPosition(submittedPicklist.Picklistnr as number, item.PosNr, item);
          }));
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (submitData.deleted.length > 0) {
        try {
          await Promise.all(
              submitData.deleted?.map((posNr) => deleteUmbuchungPosition(submittedPicklist.Picklistnr as number, posNr)),
          );
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (errors.length > 0) {
        if (errors.length === 1) {
          throw errors[0];
        } else {
          throw new Error(`Mehrere Fehler\n${errors.join('\n')}`);
        }
      }

      submittedPicklist.Status = ApiPicklistState.Todo;
      this.commitChange(submittedPicklist ?? null, ApiPicklistType.Umbuchung);
      this.reset();
    },
    async submitCorrection(submittedPicklist: Picklist) {
      const submitData = await this.getCorrectionSubmitData();
      const errors: Error[] = [];

      if (submitData.type === 'create') {
        await submitCorrection(submitData.create);
        submittedPicklist.Status = ApiPicklistState.Todo;
        this.commitChange(submittedPicklist ?? null, ApiPicklistType.Correction);
        this.reset();
        return;
      }

      if (submitData.added.length > 0) {
        try {
          console.log('Create Pos: ', submitData.added);
          for (const position of submitData.added) {
            await postCorrectionPosition(submittedPicklist.Picklistnr as number, position);
          }
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (submitData.modified.length > 0) {
        try {
          await Promise.all(submitData.modified.map(async (item) => {
            console.log('Update Pos: ', item);
            await putCorrectionPosition(submittedPicklist.Picklistnr as number, item.PosNr, item);
          }));
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (submitData.deleted.length > 0) {
        try {
          await Promise.all(
              submitData.deleted?.map((posNr) => deleteCorrectionPosition(submittedPicklist.Picklistnr as number, posNr)),
          );
        } catch (err: any) {
          errors.push(err);
        }
      }

      if (errors.length > 0) {
        if (errors.length === 1) {
          throw errors[0];
        } else {
          throw new Error(`Mehrere Fehler\n${errors.join('\n')}`);
        }
      }

      submittedPicklist.Status = ApiPicklistState.Todo;
      this.commitChange(submittedPicklist ?? null, ApiPicklistType.Correction);
      this.reset();
    },
    async park(picklist?: Picklist) {
      this.parking = true;
      try {
        // Immer die vorgegebene Pickliste benutzen, wenn vorhanden
        const parkedPicklist = this.picklist ?? picklist;
        if (!parkedPicklist) throw new Error('Keine abzubrechende Pickliste gegeben');
        if (parkedPicklist.isIncoming) return; // Pickliste eingehend, also nicht parken
        await parkPicklist(parkedPicklist, this.getOutgoingSubmitData() as ApiPicklistSubmitData);
        parkedPicklist.Status = ApiPicklistState.Parked;
        await this.commitChange(parkedPicklist);
        this.reset();
      } finally {
        this.parking = false;
      }
    },
    getPickData(position: Position) {
      let item = this.picked[position.uniqueIndex];

      if (item === undefined) {
        item = (this.picked[position.uniqueIndex] = new PickData({position, bookings: []}));
      }

      return item;
    },
    deletePickData(uniqueIndex: string) {
      const allPickData = {...this.picked};
      delete allPickData[uniqueIndex];

      this.picked = allPickData;
    },
    getBooking(position: Position, bookingIndex?: number, createIfMissing: boolean = false) {
      const settingsStore = useSettings();
      if ((settingsStore.selectedSubsidiary ?? null) === null) throw new Error('Bitte Niederlassung auswählen');
      const item = this.getPickData(position);

      let bookingItem: Booking;
      if (typeof bookingIndex !== 'number' || (item.bookings.length === 0 && bookingIndex === 0) || createIfMissing) {
        if (item.bookings.length > (bookingIndex ?? 0) && // existiert
          (item.bookings[item.bookings.length - 1].isEmpty || createIfMissing) // leer oder darf leer neu erstellt sein
        ) {
          return item.bookings[item.bookings.length - 1];
        }

        function getDefaultQty(): number {
          switch (position.ArtikelArt) {
            case '8000':
            case '9000':
            case '9900':
              return position.Menge;
            default:
              return 0;
          }
        }

        bookingItem = new Booking({
          qty: getDefaultQty(),
          batchNo: null,
          serialNo: null,
          nve: null,
          warehouse: this.picklist?.isUmbuchung ?
            this.picklist.NiederlassungAbgehend!.Niederlassung === settingsStore.selectedSubsidiary ?
              settingsStore.selectedWarehouse!.Lagerhalle : null :
            settingsStore.selectedWarehouse!.Lagerhalle,
          subsidiary: this.picklist?.isUmbuchung ? this.picklist.NiederlassungAbgehend!.Niederlassung : settingsStore.selectedSubsidiary!,
          destinationSubsidiary: this.picklist?.isUmbuchung ? this.picklist.NiederlassungEingehend!.Niederlassung : undefined,
          shelf: null,
          position,
          generateNve: false,
          acknowledgementNumber: null,
          dealerBatchNo: null,
          bestBeforeDate: null,
          manufacturingDate: null,
          picklistType: this.picklist?.Art ?? ApiPicklistType.Order,
        });
        item.bookings.push(bookingItem);
      } else {
        bookingItem = item!.bookings[bookingIndex];
        if (!bookingItem) throw new Error('Diese Buchung existiert nicht!');
      }

      return bookingItem;
    },
    deleteBooking(position: Position, bookingIndex: number) {
      const item = this.getPickData(position);

      item.bookings.splice(bookingIndex, 1);
    },
    clearBookings(position: Position) {
      const item = this.getPickData(position);

      item.bookings.length = 0;
      this.getBooking(position, 0);
    },
    modify({
      position,
      bookingIndex,
      qty,
      addQty,
      serialNo,
      subsidiary,
      nve,
      batchNo,
      warehouse,
      shelf,
      generateNve,
      acknowledgementNumber,
      dealerBatchNo,
      bestBeforeDate,
      manufacturingDate,
      destinationWarehouse,
      destinationSubsidiary,
      destinationShelf,
    }: ModifyOptions) {
      console.log('modify: ', this.selectedPosition);
      const item = this.getBooking(position, bookingIndex);

      // NVE-Zeugs
      if (nve) {
        const foundNve = position.VerfuegbareNVEsCache[nve];
        if (foundNve) {
          const remainingQty = position.Menge - (this.getPickData(position).bookings?.reduce((cum, b) => cum + b.qty, 0) ?? 0);
          // Menge aus NVE übernehmen
          if (foundNve.Restmenge && !addQty && !item.qty) {
            const pickData = this.getPickData(position);
            // wenn selbst erstellt/noch genug vorhanden => NVE-Menge
            // wenn aus API und nicht genug vorhanden => verbleibende Menge
            addQty = (position.givenByApi || foundNve.Restmenge <= remainingQty || this.picklist?.Picklistnr === '-1') ? foundNve.Restmenge : remainingQty;
            if (this.picklist?.Picklistnr !== '-1') {
              addQty = Math.min(addQty, position.Menge - pickData.qty);
            }
          }

          // Chargennummer aus NVE übernehmen
          batchNo = foundNve.Chargennr;
        }
      }

      // Menge hinzufügen oder setzen
      if (addQty !== undefined || qty !== undefined) {
        const newQty = qty ?? item.qty + addQty!;
        item.qty = newQty;
      }

      if (serialNo !== undefined) item.serialNo = serialNo;
      if (subsidiary !== undefined) item.subsidiary = subsidiary;
      if (nve !== undefined) item.nve = nve;
      if (batchNo !== undefined) item.batchNo = batchNo;
      if (warehouse !== undefined) item.warehouse = warehouse;
      if (shelf !== undefined) item.shelf = shelf;

      // Wareneingang
      if (generateNve !== undefined) item.generateNve = generateNve;
      if (acknowledgementNumber !== undefined) item.acknowledgementNumber = acknowledgementNumber;
      if (dealerBatchNo !== undefined) item.dealerBatchNo = dealerBatchNo;
      if (bestBeforeDate !== undefined) item.bestBeforeDate = bestBeforeDate;
      if (manufacturingDate !== undefined) item.manufacturingDate = manufacturingDate;

      // Umbuchung
      if (destinationWarehouse !== undefined) item.destinationWarehouse = destinationWarehouse;
      if (destinationSubsidiary !== undefined) item.destinationSubsidiary = destinationSubsidiary;
      if (destinationShelf !== undefined) item.destinationShelf = destinationShelf;

      console.log('modify end: ', this.selectedPosition);
      console.log('modify end item: ', item);
      // needed for reactivity
      this.picked = {...this.picked};
      this.eventEmitter.dispatchEvent(new Event('modify'));
      console.log('real end: ', this.selectedPosition);
    },
    async modifyByScan(options: ModifyByScanOptions) {
      if (!this.picklist) throw new Error('Es ist keine Pickliste ausgewählt');
      let position: Position | null = null;
      if (options.shelf && options.shelf > INT_MAX) throw new InvalidInputError('Lagerfach-Nr. zu groß');
      if (options.shelf && String(options.shelf).length > 10) throw new InvalidInputError('Lagerfach-Nr. zu lang');
      if (options.nve && options.nve.length !== 18) throw new InvalidInputError('ungültige NVE');
      if (String(options.batchNo).length > 9) throw new InvalidInputError('ungültige Chargennummer');
      const copiedOptions = structuredClone(options);

      let bookingIndex: number | undefined = undefined;
      let booking: Booking | null = null;

      // Aktuelle Position gleiche NVE? Dann diese Position übernehmen (Wichtig für mehrere Positionen mit gleicher Artikelnummer)
      let checkPositions = Object.entries(this.picklist.allPositions);
      if (copiedOptions.nve && this.selectedPosition) {
        const selectedPosition = this.picklist.allPositions[this.selectedPosition];
        if (selectedPosition.VerfuegbareNVEs.find((nve: ApiNVE) => nve.NVE == copiedOptions.nve)) {
          checkPositions = [[String(this.selectedPosition), selectedPosition]];
        }
      }

      // Betroffene Position ermitteln
      for (const [i, currentPosition] of checkPositions) {
        if (this.picklist.isInventory && i !== String(this.selectedPosition)) continue;
        const pickData = this.getPickData(currentPosition);
        const bookingCount = pickData.bookings.length;
        bookingIndex = bookingCount > 0 ? bookingCount - 1 : undefined;

        // Wenn NVE/Chargennr in letzer Buchung gesetzt und neue NVE/Chargennr gescannt wurde, dann neue Buchung anlegen
        if (bookingIndex !== undefined && (copiedOptions.nve || copiedOptions.batchNo || copiedOptions.dealerBatchNo)) {
          booking = pickData.bookings[bookingIndex];

          // NVE und Chargennummer sind gesetzt und stimmen nicht überein => neue Buchung
          if (
            (booking.nve && booking.nve !== copiedOptions.nve) ||
            (booking.batchNo && booking.batchNo !== copiedOptions.batchNo) ||
            (booking.dealerBatchNo && booking.dealerBatchNo !== copiedOptions.dealerBatchNo)
          ) {
            // darf keine neue Buchung hinzufügen => weitersuchen
            if (!this.picklist.mayAddBooking) continue;
            bookingIndex = undefined;
          }
        }

        // Position anhand der NVE ermitteln
        if (copiedOptions.nve) {
          const foundNve = currentPosition.VerfuegbareNVEsCache[copiedOptions.nve];
          if (foundNve && foundNve.MHD && currentPosition.VerfuegbareNVEsCache &&
            !pickData.bookings.some((booking) => booking.nve === foundNve.NVE)) {
            // Älteste NVE(s) (MHD) ermitteln
            const oldestMhd = Object.entries(currentPosition.VerfuegbareNVEsCache).reduce((oldestMhd, [, nve]) => {
              if (nve === undefined) return oldestMhd;
              const existsInPosition = pickData.bookings.some((booking) => booking.nve === nve.NVE);
              if (nve.NVE && !existsInPosition &&
                (!oldestMhd || nve.MHDParsed?.isBefore(oldestMhd))) {
                return nve.MHDParsed ?? null;
              }
              return oldestMhd;
            }, null as Moment | null)?.toISOString();

            const oldestNves = Object.fromEntries(Object.entries(currentPosition.VerfuegbareNVEsCache)
                .filter(([, nve]) => oldestMhd == nve?.MHDParsed?.toISOString()));

            // Auf NVE mit älterer MHD prüfen
            if (oldestMhd && oldestMhd !== foundNve.MHDParsed?.toISOString()) {
              const olderNve = Object.values(oldestNves).find(() => true);

              // Ton abspielen bei Confirmation Popup
              NativeAudio.play({
                assetId: 'action-negative',
                time: 0.00,
              });

              if (!await doConfirm(`Es wurde eine NVE mit einem älteren MHD gefunden:
NVE: ${olderNve?.NVE}
MHD: ${olderNve?.MHDParsed?.format('DD.MM.YYYY')}
Soll die NVE ${copiedOptions.nve} trotzdem beibehalten werden?`, 'NVE mit älterem MHD gefunden', {
                ok: 'Ja',
                cancel: 'Nein',
              })) {
                // Scan abbrechen
                throw new UserAbortError('Scan abgebrochen');
                return;
              }
            }
          }
          if (foundNve) {
            position = currentPosition;
            copiedOptions.addQty = copiedOptions.addQty ?? 0;

            // Chargennummer der NVE übernehmen
            if (foundNve.Chargennr) {
              copiedOptions.batchNo = foundNve.Chargennr;
            }

            copiedOptions.subsidiary = foundNve.Niederlassung;
            copiedOptions.warehouse = foundNve.Lagerhalle;
            copiedOptions.shelf = foundNve.Lagerfach;

            // Menge aus NVE übernehmen, maximal Restmenge der Position
            const remainingQty = position.Menge - (pickData.bookings?.reduce((cum, b) => cum + b.qty, 0) ?? 0);
            if (!this.picklist.allowOvershoot && remainingQty <= 0) {
              // Überbuchen nicht möglich + Position voll => weitersuchen
              continue;
            }
            this.selectedPosition = Number(i);

            if (this.picklist.isUmbuchung) {
              copiedOptions.addQty = foundNve.Restmenge;
              if (foundNve.Niederlassung === this.picklist.NiederlassungAbgehend?.Niederlassung) {
                copiedOptions.subsidiary = foundNve.Niederlassung;
                copiedOptions.warehouse = foundNve.Lagerhalle;
                copiedOptions.shelf = foundNve.Lagerfach;
              } else if (foundNve.Niederlassung !== this.picklist.NiederlassungAbgehend?.Niederlassung &&
                this.selectedPosition === 0 && this.picklist.allPositions.length === 1 &&
                ((booking?.subsidiary == null && booking?.warehouse == null) ||
                await doConfirm('Die Niederlassung der NVE stimmt nicht mit der gewählten Abgehenden Niederlassung überein. Soll diese angepasst werden?'))) {
                copiedOptions.subsidiary = foundNve.Niederlassung;
                copiedOptions.warehouse = foundNve.Lagerhalle;
                copiedOptions.shelf = foundNve.Lagerfach;
                this.picklist.NiederlassungAbgehend!.Niederlassung = copiedOptions.subsidiary!;
                this.picklist.NiederlassungAbgehend!.Name1 = useSubsidiariesStore().data.find((sub) => sub.NiederlassungNr === copiedOptions.subsidiary)?.Name1 ?? '';
              } else {
                await doAlert('Niederlassung der NVE stimmt nicht mit der Niederlassung des abgehenden Lagers überein. NVE wird nicht übernommen.', 'Achtung');
                await this.deletePosition(position.uniqueIndex);
                return;
              }
            } else if (this.picklist.isCorrection) {
              copiedOptions.addQty = foundNve.Restmenge;
              copiedOptions.warehouse = foundNve.Lagerhalle;
              copiedOptions.shelf = foundNve.Lagerfach;
            }
            break;
          }
        }

        if (copiedOptions.ean) {
          // Position anhand der EAN ermitteln
          if (currentPosition.Eancode === copiedOptions.ean) {
            position = currentPosition;
            copiedOptions.addQty = copiedOptions.addQty ?? 1;
            this.selectedPosition = Number(i);
            break;
          }

          // Position anhand der Gebinde-EAN ermitteln
          if (currentPosition.EAN_Gebinde === copiedOptions.ean) {
            position = currentPosition;
            if (copiedOptions.addQty) {
              copiedOptions.addQty *= position.Gebinhalt ?? 1;
            } else {
              copiedOptions.addQty = position.Gebinhalt ?? undefined;
            }
            this.selectedPosition = Number(i);
            break;
          }

          const foundEAN = currentPosition.ArtikelEAN?.find((a) => a.EANCode === copiedOptions.ean);
          if (foundEAN) {
            position = currentPosition;
            copiedOptions.addQty = (copiedOptions.addQty ?? 1) *
              ((foundEAN.EinheitTyp === ApiArticleEANUnitType.Multi ? currentPosition.Gebinhalt : 1) ?? 1);
            this.selectedPosition = Number(i);
            break;
          }

          const foundGebinde = currentPosition.ArtikelGebinde?.find((g) => g.EANCode === copiedOptions.ean);
          if (foundGebinde) {
            position = currentPosition;
            if (copiedOptions.addQty) {
              copiedOptions.addQty *= foundGebinde.Umrechnung ?? 1;
            } else {
              copiedOptions.addQty = foundGebinde.Umrechnung ?? undefined;
            }
            this.selectedPosition = Number(i);
            break;
          }
        }
      }

      // aktuell ausgewählte Position verwenden
      let pickData;
      // Es wurde anhand der Übergebenen NVE/EAN keine Position gefunden!
      if (position === null) {
        const errNotFound = new NotFoundError('Position konnte nicht gefunden werden');

        // aktuell offene Position darf nicht verwendet werden => Fehler
        // if (!useCurrentIfNotFound) throw errNotFound;

        // EAN oder NVE gesetzt? => keine übereinstimmende Position => Fehler
        if ((options.ean || options.nve) && !!!options.insertInCurrentPositionIfNotFound) throw errNotFound;

        const errSelectFirst = new InvalidInputError('Bitte erst eine Position wählen');
        // aktuell offene Position hat den falschen Typ => Fehler
        if (typeof this.selectedPosition !== 'number') throw errSelectFirst;

        // aktuelle Position soll es sein
        position = this.picklist.allPositions[this.selectedPosition];

        // keine aktuelle Position gefunden
        if (!position) throw errSelectFirst;

        pickData = this.getPickData(position);
        const bookingCount = pickData.bookings.length;
        bookingIndex = bookingCount > 0 ? bookingCount - 1 : undefined;
        booking = pickData.bookings[bookingIndex!];
      } else {
        pickData = this.getPickData(position);
      }

      if (this.picklist.isOutgoing && this.selectedPosition != null && this.picklist.Picklistnr != '-1') {
        const selectedPosition = this.picklist.allPositions[this.selectedPosition];
        pickData = this.getPickData(selectedPosition);

        if (pickData.full) {
          const alternativePositionOfSameArticle = checkPositions.find(
              ([i, pos]) => pos.Artikelnr === selectedPosition.Artikelnr && Number(i) != this.selectedPosition && !this.getPickData(pos).full);
          if (alternativePositionOfSameArticle) {
            this.selectedPosition = Number(alternativePositionOfSameArticle[0]);
            position = this.picklist.allPositions[this.selectedPosition];
            const bookingCount = pickData.bookings.length;
            bookingIndex = bookingCount > 0 ? bookingCount - 1 : undefined;
          } else {
            NativeAudio.play({
              assetId: 'action-negative',
              time: 0.00,
            });

            if (!await doConfirm('Alle Positionen sind bereits erfüllt, soll überbucht werden?')) return;
          }
        }
      }

      // NVE bereits in Position => Scan überspringen
      if (copiedOptions.nve) {
        if (Object.values(this.picked).some((pickData) => pickData.bookings.some((tmpbooking) => tmpbooking.nve === copiedOptions.nve))) {
          throw new InvalidInputError(`NVE ${copiedOptions.nve} bereits gebucht.`);
        }
      }
      // Menge/Charge darf bei Positionen mit NVE nicht geändert werden.
      if (!this.picklist.isIncoming && this.picklist.Picklistnr != '-1' &&
        (((copiedOptions.addQty && booking?.qty) || (copiedOptions.batchNo && booking?.batchNo)) && booking?.nve && !copiedOptions.nve)) {
        throw new InvalidInputError(`Scan ungültig.`);
      }

      if (this.picklist.isUmbuchung) { // this is not beautiful but does the trick for the current app
        if (copiedOptions.shelf) {
          if (booking?.shelf == null) {
            if (copiedOptions.subsidiary === this.picklist.NiederlassungAbgehend?.Niederlassung) {
              // do nothing, data is in right place
            } else if (copiedOptions.subsidiary !== this.picklist.NiederlassungAbgehend?.Niederlassung &&
              this.selectedPosition === 0 && this.picklist.allPositions.length === 1 &&
              ((booking?.subsidiary == null && booking?.warehouse == null) ||
              await doConfirm('Die Niederlassung des Lagerfachs stimmt nicht mit der gewählten Abgehenden Niederlassung überein. Soll diese angepasst werden?'))) {
              this.picklist.NiederlassungAbgehend!.Niederlassung = copiedOptions.subsidiary!;
              this.picklist.NiederlassungAbgehend!.Name1 = useSubsidiariesStore().data.find((sub) => sub.NiederlassungNr === copiedOptions.subsidiary)?.Name1 ?? '';
            } else {
              copiedOptions.subsidiary = undefined;
              copiedOptions.warehouse = undefined;
              copiedOptions.shelf = undefined;
              doAlert('Lager stimmt nicht mit dem ausgewählten Lager überein');
            }
          } else if (booking?.destinationShelf == null) {
            if (copiedOptions.subsidiary === this.picklist.NiederlassungEingehend?.Niederlassung) {
              copiedOptions.destinationSubsidiary = copiedOptions.subsidiary;
              copiedOptions.destinationShelf = copiedOptions.shelf;
              copiedOptions.destinationWarehouse = copiedOptions.warehouse!;
              copiedOptions.subsidiary = undefined;
              copiedOptions.warehouse = undefined;
              copiedOptions.shelf = undefined;
            } else if (copiedOptions.subsidiary !== this.picklist.NiederlassungEingehend?.Niederlassung &&
              this.selectedPosition === 0 && this.picklist.allPositions.length === 1 &&
              ((booking?.destinationSubsidiary == null && booking?.destinationWarehouse == null) ||
              await doConfirm('Die Niederlassung des Lagerfachs stimmt nicht mit der gewählten Eingehende Niederlassung überein. Soll diese angepasst werden?'))) {
              this.picklist.NiederlassungEingehend!.Niederlassung = copiedOptions.subsidiary!;
              this.picklist.NiederlassungEingehend!.Name1 = useSubsidiariesStore().data.find((sub) => sub.NiederlassungNr === copiedOptions.subsidiary)?.Name1 ?? '';

              copiedOptions.destinationSubsidiary = copiedOptions.subsidiary;
              copiedOptions.destinationShelf = copiedOptions.shelf;
              copiedOptions.destinationWarehouse = copiedOptions.warehouse!;
              copiedOptions.subsidiary = undefined;
              copiedOptions.warehouse = undefined;
              copiedOptions.shelf = undefined;
            } else {
              copiedOptions.subsidiary = undefined;
              copiedOptions.warehouse = undefined;
              copiedOptions.shelf = undefined;
              doAlert('Lager stimmt nicht mit dem ausgewählten Lager überein');
            }
          } else {
            const result = await Dialog.confirm({
              title: 'Bitte wählen:',
              message: (await Device.getInfo()).platform === 'web' ? 'Bitte wählen: Ok: Eingehendes Lager | Abbrechen: Abgehendes Lager' : '',
              okButtonTitle: 'Eingehendes Lager',
              cancelButtonTitle: 'Abgehendes Lager',
            });

            if (!result.value) { // Abgehendes Lager
              if (copiedOptions.subsidiary !== this.picklist.NiederlassungAbgehend?.Niederlassung &&
                this.selectedPosition === 0 && this.picklist.allPositions.length === 1 &&
                ((booking?.subsidiary == null && booking?.warehouse == null) ||
                await doConfirm('Die Niederlassung des Lagerfachs stimmt nicht mit der gewählten Abgehenden Niederlassung überein. Soll diese angepasst werden?'))) {
                this.picklist.NiederlassungAbgehend!.Niederlassung = copiedOptions.subsidiary!;
                this.picklist.NiederlassungAbgehend!.Name1 = useSubsidiariesStore().data.find((sub) => sub.NiederlassungNr === copiedOptions.subsidiary)?.Name1 ?? '';
              } else {
                copiedOptions.subsidiary = undefined;
                copiedOptions.warehouse = undefined;
                copiedOptions.shelf = undefined;
                doAlert('Lager stimmt nicht mit dem ausgewählten Lager überein');
              }
            } else { // Eingehendes Lager
              if (copiedOptions.subsidiary === this.picklist.NiederlassungEingehend?.Niederlassung) {
                copiedOptions.destinationSubsidiary = copiedOptions.subsidiary;
                copiedOptions.destinationShelf = copiedOptions.shelf;
                copiedOptions.destinationWarehouse = copiedOptions.warehouse!;
                copiedOptions.subsidiary = undefined;
                copiedOptions.warehouse = undefined;
                copiedOptions.shelf = undefined;
              } else if (copiedOptions.subsidiary !== this.picklist.NiederlassungEingehend?.Niederlassung &&
                this.selectedPosition === 0 && this.picklist.allPositions.length === 1 &&
                ((booking?.destinationSubsidiary == null && booking?.destinationWarehouse == null) ||
                await doConfirm('Die Niederlassung des Lagerfachs stimmt nicht mit der gewählten Eingehende Niederlassung überein. Soll diese angepasst werden?'))) {
                this.picklist.NiederlassungEingehend!.Niederlassung = copiedOptions.subsidiary!;
                this.picklist.NiederlassungEingehend!.Name1 = useSubsidiariesStore().data.find((sub) => sub.NiederlassungNr === copiedOptions.subsidiary)?.Name1 ?? '';

                copiedOptions.destinationSubsidiary = copiedOptions.subsidiary;
                copiedOptions.destinationShelf = copiedOptions.shelf;
                copiedOptions.destinationWarehouse = copiedOptions.warehouse!;
                copiedOptions.subsidiary = undefined;
                copiedOptions.warehouse = undefined;
                copiedOptions.shelf = undefined;
              } else {
                copiedOptions.subsidiary = undefined;
                copiedOptions.warehouse = undefined;
                copiedOptions.shelf = undefined;
                doAlert('Lager stimmt nicht mit dem ausgewählten Lager überein');
              }
            }
          }
        }
      }

      this.modify({bookingIndex, ...copiedOptions, position});
    },
    async addPosition(position: Position) {
      if (!this.picklist?.mayAddPosition) throw new Error('Es dürfen keine Positionen hinzugefügt werden');
      for (const [i, currentPosition] of (this.picklist?.allPositions as Position[]).entries()) {
        if (position.Eancode === currentPosition.Eancode && !this.picklist.isUmbuchung) {
          if (!this.picklist.isInventory && !await doConfirm('Der gewählte Artikel wurde bereits erfasst.\nSoll er wirklich noch einmal erfasst werden?', 'Bereits erfasst')) {
            this.selectedPosition = i;
            return;
          } else {
            break;
          }
        }
      }
      this.picklist?.allPositions.push(position);
      this.selectedPosition = this.picklist ? this.picklist?.allPositions.length - 1 : null;
      this.eventEmitter.dispatchEvent(new Event('add-position'));
    },
    checkUmbuchungDuplicatePosition(positionToAdd: Position) {
      if (this.picklist?.allPositions.find((pos) => pos.AbgehendesLager?.Chargennr === positionToAdd.AbgehendesLager?.Chargennr) &&
          this.picklist?.allPositions.find((pos) => pos.NVE === positionToAdd.NVE)) {
        return true;
      }
      return false;
    },
    async addPositionFromArticle(article: ApiArticle) {
      if (!this.picklist) throw new Error('no picklist given');
      let maxPosNr = this.picklist.maxPosNr ?? 0;
      let lieferantenName = this.picklist.lieferantName ?? '';
      let Belegnr = this.picklist.BestellNr;
      this.picklist.allPositions.forEach((a) => {
        if (maxPosNr < a.Posnr) maxPosNr = a.Posnr;
        lieferantenName = a.Name;
        Belegnr = a.Belegnr;
      });

      const palette = article.ArtikelPaletten[0];
      const position: ApiPosition = {
        Posnr: maxPosNr + 10,
        Artikelnr: article.ArtikelNr,
        Eancode: article.EANCode,
        Bezeichn1: article.Bezeichnung1,
        Bezeichn2: article.Bezeichnung2,
        ArtikelArt: article.ArtikelArt,
        GruppenBezeichnung: article.GruppeBezeichnung,
        Menge: 0,
        Einheit: article.EinheitBezeichnung,
        Gebindemenge: 0,
        Gebindeinh: article.HauptGebinde?.GebindeBezeichnung ?? null,
        Gebinhalt: article.HauptGebinde?.GebindeInhalt ?? null,
        GebindeSchluessel: article.HauptGebinde?.GebindeSchluessel ?? 0,
        EAN_Gebinde: article.ArtikelGebinde[0]?.EANCode ?? null,
        Palettenmenge: 0,
        Umrpalgebinde: palette?.GebindeAnzahl ?? null,
        Umrpalmenge: palette?.GebindeAnzahl * palette?.GebindeInhalt,
        Palettennr: palette?.PalettenNr,
        Chargenpflicht: this.picklist.isSuggestion ? 'N' : article.ChargenNummer,
        Seriennrpflicht: this.picklist.isSuggestion ? 'N' : article.SerienNummer,
        Datum: this.picklist.Erstelltam,
        Name: lieferantenName,
        Belegnr: Belegnr ?? 0,
        VerfuegbareNVEs: this.picklist.isInventory || this.picklist.isUmbuchung || this.picklist.isCorrection || this.picklist.Picklistnr === '-1' ? article.VerfuegbareNVEs ?? [] : [],
        Buchungen: [],
        // eslint-disable-next-line max-len
        Chargen: this.picklist.isInventory || this.picklist.isUmbuchung || this.picklist.isCorrection ? article.VerfuegbareChargen?.Chargen ?? [] : [],
        Restmenge: 0,
        NVE: '',
        Matchcode: '',
        Bediener: this.picklist?.Kmsbediener ?? '',
        ArtikelEAN: article.ArtikelEAN,
        ArtikelGebinde: article.ArtikelGebinde,
        ArtikelPaletten: article.ArtikelPaletten,
      };

      await this.addPosition(Position.fromApi(position, false));
    },
    async deletePosition(index: string) {
      if (!this.picklist?.mayAddPosition) throw new Error('Es dürfen keine Positionen gelöscht werden');
      const foundIndex = this.picklist.allPositions.findIndex((p) => p.uniqueIndex === index);
      if (foundIndex === -1) throw new Error('Diese Position existiert nicht');
      this.picklist.allPositions.splice(foundIndex, 1);
      this.deletePickData(index);
      this.eventEmitter.dispatchEvent(new Event('delete-position'));
    },
    getIncomingSubmitData(): ApiEingangslieferscheinSubmitData {
      const picklist = this.picklist;
      if (!picklist) throw new Error('no picklist set');
      const settingsStore = useSettings();
      if ((settingsStore.selectedSubsidiary ?? null) === null) throw new Error('Bitte Niederlassung auswählen');
      const Bediener = useSettings().userShort ?? 'SYS';

      const Positionen = Object.values(picklist.allPositions)
          .reduce<ApiEingangslieferscheinSubmitPosition[]>((collected, position) => {
            const item = this.picked[position.uniqueIndex];

            for (const booking of item.bookings!) {
              if (booking.qty === 0) continue;

              collected.push({
                PosNr: position.Posnr,
                Menge: booking.qty,
                GebindeMG: booking.qtyGebinde,
                NVE: booking.nve ? [booking.nve] : [],
                EingehendesLager: {
                  Lagerfach: booking.shelf ?? undefined,
                  Niederlassung: booking.subsidiary!,
                  Lagerhalle: booking.warehouse!,
                },
                Charge: {
                  ChargenNrLief: booking.dealerBatchNo,
                  Anerkennungsnr: booking.acknowledgementNumber,
                  MHD: booking.bestBeforeDate,
                  Herstellungsdatum: booking.manufacturingDate,
                },
                NVEErzeugen: booking.generateNve,
                EANCode: position.Eancode,
                Beschreibung: position.Bezeichn1,
                ArtikelNr: position.Artikelnr,
                SerienNr: booking.serialNo,
                Gewicht: null,
                PalettenAnzahl: booking.qtyPaletten ? Math.ceil(booking.qtyPaletten) : undefined,
                PalettenNr: position.Palettennr,
              });
            }

            return collected;
          }, []);
      return {
        BestellNr: picklist.BestellNr ?? 0,
        LieferantenNr: picklist.LieferantenNr ?? null,
        ErstelltDurch: Bediener,
        Datum: picklist.Kmsam,
        GeplLiefDatum: picklist.Kmsdatum ? picklist.Kmsdatum : null,
        Niederlassung: settingsStore.selectedSubsidiary!,
        Positionen,
      };
    },
    getOutgoingSubmitData(): ApiPicklistSubmitData {
      // Warenausgang
      const Bediener = useSettings().userShort ?? 'SYS';

      const Positionen =
        Object.values(this.picked).reduce<ApiBooking[]>(
            (collected, p) => {
              if (!p.position.Belegnr || !p.position.Posnr) return collected;

              for (const booking of p.bookings) {
                if (booking.qty === 0) continue;

                const EingehendesLager: ApiBookingWarehouse | null =
                this.picklist?.Art === ApiPicklistType.Internal && p.position.Eingehendeslager ?
                  {
                    ...p.position.Eingehendeslager,
                    Chargennr: booking.batchNo ?? undefined,
                  } :
                  null;

                collected.push({
                  Belegnr: p.position.Belegnr,
                  Posnr: p.position.Posnr,
                  Menge: booking.qty,
                  Gebindemenge: p.position.Gebinhalt ? Math.round((booking.qty / (p.position.Gebinhalt ?? 1)) * 100) / 100 : 0,
                  NVE: booking.nve ?? undefined,
                  AbgehendesLager: {
                    Lagerfach: booking.shelf ?? undefined,
                    Niederlassung: booking.subsidiary!,
                    Lagerhalle: booking.warehouse!,
                    Chargennr: booking.batchNo ?? undefined,
                  // TODO: Seriennummer
                  },
                  EingehendesLager,
                });
              }

              return collected;
            }, [],
        );
      return {Positionen, Bediener};
    },
    getOutgoingCreateData(): ApiPicklistSubmitData {
      // Warenausgang
      const Bediener = useSettings().userShort ?? 'SYS';
      console.log(this.picked);

      const Positionen =
        Object.values(this.picked).reduce<ApiBooking[]>(
            (collected, p) => {
              for (const booking of p.bookings) {
                if (booking.qty === 0) continue;

                collected.push({
                  ArtikelNr: p.position.Artikelnr,
                  Posnr: p.position.Posnr,
                  Menge: booking.qty,
                  Gebindemenge: p.position.Gebinhalt ? Math.round((booking.qty / (p.position.Gebinhalt ?? 1)) * 100) / 100 : 0,
                  NVE: booking.nve ?? undefined,
                  AbgehendesLager: {
                    Lagerfach: booking.shelf ?? undefined,
                    Niederlassung: booking.subsidiary!,
                    Lagerhalle: booking.warehouse!,
                    Chargennr: booking.batchNo ?? undefined,
                  // TODO: Seriennummer
                  },
                });
              }

              return collected;
            }, [],
        );
      return {Positionen, KundenNr: this.picklist?.KundenNr ?? undefined, Bediener};
    },
    async getInventorySubmitData() {
      const picklist = this.picklist;
      const settingsStore = useSettings();
      if (!picklist) throw new Error('no picklist set');
      let apiInventory;
      if (picklist.Picklistnr) {
        if (!picklist.Picklistnr) throw new Error('no Picklistnr given');
        [apiInventory] = await getDetailInventory({Belegnr: picklist.Picklistnr as number});
        if (!apiInventory) throw new Error('Diese Inventur konnte nicht gefunden werden');
      } else {
        apiInventory = new Inventory();
      }

      const deleted: ApiInventoryDeleteDatum[] = [];
      const added: ApiInventoryPositionReduced[] = [];
      const modified: ApiInventoryPositionReduced[] = [];
      /**
       * current data found in api position
       * `undefined` = current data not found => new position
       */
      const foundInApi: Record<string, true> = {};
      for (const apiInventoryPositionRaw of apiInventory.convertedPositions) {
        const apiInventoryPosition = Position.fromApi(apiInventoryPositionRaw);
        const reducedApiInventoryPosition = [apiInventoryPosition.toReducedInventoryPosition(apiInventory)];
        /**
         * api position found in current data;
         * `false` = api position not found => deleted position
         */
        let foundInCurrent = false;
        for (const position of picklist.allPositions) {
          if (position.uniqueIndex !== apiInventoryPosition.uniqueIndex) continue;
          const reducedPosition =
            this.picked[position.uniqueIndex]?.bookings.map((b) => position.toReducedInventoryPosition(picklist, b)) ??
            [position.toReducedInventoryPosition(picklist)];
          foundInCurrent = true;
          foundInApi[position.uniqueIndex] = true;
          if (JSON.stringify(reducedApiInventoryPosition) !== JSON.stringify(reducedPosition)) {
            modified.push(...reducedPosition);
          }
        }
        if (!foundInCurrent) {
          deleted.push(...reducedApiInventoryPosition.map((p) => ({
            BelegNr: picklist.Picklistnr as number,
            PosNr: p.PosNr,
          })));
        }
      }

      for (const position of picklist.allPositions) {
        const reducedPosition =
          this.picked[position.uniqueIndex]?.bookings.map((b) => position.toReducedInventoryPosition(picklist, b)) ??
          [position.toReducedInventoryPosition(picklist)];
        if (!foundInApi[position.uniqueIndex]) added.push(...reducedPosition);
      }


      return {
        header: {
          BelegNr: picklist.Picklistnr as number,
          Datum: picklist.Kmsdatum,
          Bediener: useSettings().userShort ?? 'SYS',
          Niederlassung: settingsStore.selectedSubsidiary ?? 0,
        },
        deleted,
        added,
        modified,
      };
    },
    async getUmbuchungSubmitData(): Promise<UmbuchungSubmitData> {
      const picklist = this.picklist;
      const settingsStore = useSettings();
      if (!picklist) throw new Error('no picklist set');

      if (!(Number(picklist.Picklistnr) > 0)) {
        return {
          type: 'create',
          create: {
            Bediener: settingsStore.userShort ?? 'SYS',
            Datum: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            NiederlassungEingehend: {
              Niederlassung: picklist.NiederlassungEingehend!.Niederlassung!,
            },
            NiederlassungAbgehend: {
              Niederlassung: picklist.NiederlassungAbgehend!.Niederlassung!,
            },
            Positionen: Object.values(this.picked).reduce((cum, cur) => {
              cur.bookings.forEach((b) => {
                const position = b.position;
                cum.push(position.toUmbuchungPosition(picklist, b));
              });
              return cum;
            }, [] as ApiUmbuchungPosition[]),
          },
        };
      }
      // const [apiUmbuchung] = await getDetailUmbuchung( {BelegNr: picklist.Picklistnr as number});
      const [apiUmbuchungPositionen] = await getUmbuchungPositionen({BelegNr: picklist.Picklistnr as number});

      const added: ApiUmbuchungSubmitPosition[] = [];
      const deleted: number[] = [];
      const modified: ApiUmbuchungSubmitPosition[] = [];

      const allIndices = new Set<number>();
      const collectedNewPositions = Object.values(this.picked).reduce((cum, cur) => {
        cur.bookings.forEach((b) => {
          const position = b.position;
          console.log('Pos: ', b.position);
          allIndices.add(b.position.Posnr);
          cum[b.position.Posnr] = {
            PosNr: position.Posnr,
            ArtikelNr: position.Artikelnr,
            ArtikelBezeichnung1: position.Bezeichn1,
            ArtikelBezeichnung2: position.Bezeichn2,
            NVE: b.nve ?? undefined,
            Menge: b.qty,
            AbgehendesLager: {
              Lagerhalle: b.warehouse!,
              Niederlassung: b.subsidiary!,
              Lagerfach: b.shelf ?? undefined,
              Chargennr: b.batchNo ?? undefined,
            },
            EingehendesLager: {
              Lagerhalle: b.destinationWarehouse as number,
              Niederlassung: b.destinationSubsidiary as number,
              Lagerfach: b.destinationShelf ?? undefined,
              Chargennr: b.batchNo ?? undefined,
            },
            Nabi: position.Nabi ?? undefined,
            Gebinde: {
              GebindeMenge: position.Gebindemenge,
              GebindeEinheit: position.Gebindeinh,
              GebindeSchluessel: position.GebindeSchluessel,
              GebindeInhalt: position.Gebinhalt,
              EAN_Gebinde: position.EAN_Gebinde,
            },
          };
        });
        return cum;
      }, {} as Record<number, ApiUmbuchungSubmitPosition>);

      const collectedOldPositions = apiUmbuchungPositionen.reduce((cum, cur) => {
        const {
          PosNr,
          ArtikelNr,
          Menge,
          AbgehendesLager,
          EingehendesLager,
          ArtikelBezeichnung1,
          ArtikelBezeichnung2,
          Gebinde,
          Nabi,
        } = cur;
        allIndices.add(PosNr);
        cum[PosNr] = {
          PosNr,
          ArtikelNr,
          Menge,
          AbgehendesLager,
          EingehendesLager,
          ArtikelBezeichnung1,
          ArtikelBezeichnung2,
          Gebinde,
          Nabi,
        };
        return cum;
      }, {} as Record<number, ApiUmbuchungSubmitPosition>);

      console.log('New Positions: ', collectedNewPositions);
      console.log('Old Positions: ', collectedOldPositions);

      for (const index of allIndices.values()) {
        const newPosition = collectedNewPositions[index];
        const oldPosition = collectedOldPositions[index];
        if (newPosition) {
          if (oldPosition) {
            if (JSON.stringify(newPosition) === JSON.stringify(oldPosition)) {
              // gespeichert und neu identisch => nichts tun
              continue;
            }

            // gespeichert und neu unterschiedlich => verändern
            modified.push(newPosition);
            continue;
          }

          // neu => hinzufügen
          added.push(newPosition);
          continue;
        }

        if (oldPosition) {
          // nur in alten Daten gefunden => löschen
          deleted.push(index);
        }
      }

      return {
        type: 'modify',
        added,
        deleted,
        modified,
      };
    },
    async getCorrectionSubmitData(): Promise<CorrectionSubmitData> {
      const picklist = this.picklist;
      const settingsStore = useSettings();
      if (!picklist) throw new Error('no picklist set');

      if (!(Number(picklist.Picklistnr) > 0)) {
        return {
          type: 'create',
          create: {
            Bediener: settingsStore.userShort ?? 'SYS',
            Datum: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            Niederlassung: settingsStore.selectedSubsidiary!,
            Positionen: Object.values(this.picked).reduce((cum, cur) => {
              cur.bookings.forEach((b) => {
                const position = b.position;
                cum.push(position.toCorrectionPosition(picklist, b));
              });
              return cum;
            }, [] as ApiCorrectionPosition[]),
          },
        };
      }
      const [apiCorrectionPositionen] = await getCorrectionPositionen({BelegNr: picklist.Picklistnr as number});

      const added: ApiBestandskorrekturSubmitPosition[] = [];
      const deleted: number[] = [];
      const modified: ApiBestandskorrekturSubmitPosition[] = [];

      const allIndices = new Set<number>();
      const collectedNewPositions = Object.values(this.picked).reduce((cum, cur) => {
        cur.bookings.forEach((b) => {
          const position = b.position;
          console.log('Pos: ', b.position);
          allIndices.add(b.position.Posnr);
          cum[b.position.Posnr] = {
            PosNr: position.Posnr,
            ArtikelNr: position.Artikelnr,
            ArtikelBezeichnung1: position.Bezeichn1,
            ArtikelBezeichnung2: position.Bezeichn2,
            NVE: b.nve ?? undefined,
            Niederlassung: b.subsidiary!,
            Lagerhalle: b.warehouse!,
            Lagerfach: b.shelf ?? undefined,
            IstMenge: {
              TatsaechlMenge: b.qty,
            },
            Nabi: position.Nabi ? 'J' : 'N' ?? undefined,
            Gebinde: {
              GebindeMenge: position.Gebindemenge,
              GebindeEinheit: position.Gebindeinh,
              GebindeSchluessel: position.GebindeSchluessel,
              GebindeInhalt: position.Gebinhalt,
              EAN_Gebinde: position.EAN_Gebinde,
            },
          };
        });
        return cum;
      }, {} as Record<number, ApiBestandskorrekturSubmitPosition>);

      const collectedOldPositions = apiCorrectionPositionen.reduce((cum, cur) => {
        const {
          ArtikelGruppe,
          Bediener,
          BelegNr,
          Chargenpflicht,
          Datum,
          EanCode,
          Erledigt,
          GruppeBezeichnung,
          SerienNrpflicht,
          Uhrzeit,
          VerfuegbareChargen,
          VerfuegbareNVEs,
          PosNr,
          ArtikelNr,
          IstMenge,
          Niederlassung,
          Lagerhalle,
          Lagerfach,
          ArtikelBezeichnung1,
          ArtikelBezeichnung2,
          Gebinde,
          Nabi,
        } = cur;
        allIndices.add(PosNr);
        cum[PosNr] = {
          ArtikelGruppe,
          Bediener,
          BelegNr,
          Chargenpflicht,
          Datum,
          EanCode,
          Erledigt,
          GruppeBezeichnung,
          SerienNrpflicht,
          Uhrzeit,
          VerfuegbareChargen,
          VerfuegbareNVEs,
          PosNr,
          ArtikelNr,
          IstMenge,
          Niederlassung,
          Lagerhalle,
          Lagerfach,
          ArtikelBezeichnung1,
          ArtikelBezeichnung2,
          Gebinde,
          Nabi,
        };
        return cum;
      }, {} as Record<number, ApiCorrectionPosition>);

      console.log('New Positions: ', collectedNewPositions);
      console.log('Old Positions: ', collectedOldPositions);

      for (const index of allIndices.values()) {
        const newPosition = collectedNewPositions[index];
        const oldPosition = collectedOldPositions[index];
        if (newPosition) {
          if (oldPosition) {
            if (JSON.stringify(newPosition) === JSON.stringify(oldPosition)) {
              // gespeichert und neu identisch => nichts tun
              continue;
            }

            // gespeichert und neu unterschiedlich => verändern
            modified.push(newPosition);
            continue;
          }

          // neu => hinzufügen
          added.push(newPosition);
          continue;
        }

        if (oldPosition) {
          // nur in alten Daten gefunden => löschen
          deleted.push(index);
        }
      }

      return {
        type: 'modify',
        added,
        deleted,
        modified,
      };
    },
    async getSuggestionSubmitData(): Promise<SuggestionSubmitData> {
      const picklist = this.picklist;
      const settingsStore = useSettings();
      if (!picklist) throw new Error('no picklist set');

      const [apiSuggestion] = await getDetailSuggestion({BuchungNr: picklist.Picklistnr as number});
      if (!apiSuggestion) {
        return {
          type: 'create',
          create: {
            Bediener: settingsStore.userShort ?? 'SYS',
            Datum: picklist.Erstelltam,
            Positionen: Object.values(this.picked).reduce((cum, cur) => {
              cur.bookings.forEach((b) => {
                const position = b.position;
                cum.push(position.toSuggestionPosition(picklist, b));
              });
              return cum;
            }, [] as ApiSuggestionPosition[]),
          },
        };
      }

      const added: ApiSuggestionSubmitPosition[] = [];
      const deleted: number[] = [];
      const modified: ApiSuggestionSubmitPosition[] = [];

      const allIndices = new Set<number>();

      const collectedNewPositions = Object.values(this.picked).reduce((cum, cur) => {
        cur.bookings.forEach((b) => {
          const position = b.position;
          allIndices.add(b.position.Posnr);
          cum[b.position.Posnr] = {
            PosNr: position.Posnr,
            ArtikelNr: position.Artikelnr,
            Menge: b.qty,
            GebindeMenge: b.qtyGebinde ?? undefined,
            Niederlassung: b.subsidiary!,
            Lagerfach: b.shelf ?? 0,
            Lagerhalle: b.warehouse!,
          };
        });
        return cum;
      }, {} as Record<number, ApiSuggestionSubmitPosition>);

      const collectedOldPositions = apiSuggestion.BestellVorschlagPositionen.reduce((cum, cur) => {
        const {
          PosNr,
          ArtikelNr,
          Menge,
          GebindeMenge,
          Niederlassung,
          Lagerfach,
          Lagerhalle,
        } = cur;
        allIndices.add(PosNr);
        cum[PosNr] = {
          PosNr,
          ArtikelNr,
          Menge,
          GebindeMenge,
          Niederlassung,
          Lagerfach,
          Lagerhalle,
        };
        return cum;
      }, {} as Record<number, ApiSuggestionSubmitPosition>);

      for (const index of allIndices.values()) {
        const newPosition = collectedNewPositions[index];
        const oldPosition = collectedOldPositions[index];
        if (newPosition) {
          if (oldPosition) {
            if (JSON.stringify(newPosition) === JSON.stringify(oldPosition)) {
              // gespeichert und neu identisch => nichts tun
              continue;
            }

            // gespeichert und neu unterschiedlich => verändern
            modified.push(newPosition);
            continue;
          }

          // neu => hinzufügen
          added.push(newPosition);
          continue;
        }

        if (oldPosition) {
          // nur in alten Daten gefunden => löschen
          deleted.push(index);
        }
      }

      return {
        type: 'modify',
        added,
        deleted,
        modified,
      };
    },
    validate(): boolean {
      const allPositions = this.picklist?.allPositions ?? [];
      if (!Object.values(this.picked).some((p) => p.qty > 0)) {
        // no items at all
        this.selectedPosition = 0;
        return false;
      }
      // eslint-disable-next-line guard-for-in
      for (const i in allPositions) {
        const currentPosition = this.picklist!.allPositions[Number(i)];
        const pickData = this.getPickData(currentPosition);

        if (!pickData.valid && pickData.qty > 0) {
          // missing batch no etc.
          this.selectedPosition = Number(i);
          return false;
        }
      }

      return true;
    },
  },
  getters: {
    picklist(): Picklist | null {
      const plStore = useDetailPicklistStore();
      return plStore.data;
    },
    totalItems(): number {
      return this.picklist?.allPositions.length ?? 0;
    },
    completedItems(): number {
      return Object.values(this.picked)
          .filter(
              (p) => p.done,
          ).length;
    },
    remainingItems(): number {
      return this.totalItems - this.completedItems;
    },
    // Anzahl hinzugefügte Unteritems (zur Unterscheidung: komplett leer oder angefangene Positionen?)
    addedItems(): number {
      return Object.values(this.picked).reduce((cum, p) => cum + p.bookings.reduce((cumI, b) => cumI + b.qty, 0), 0);
    },
    completionPercentage(): number {
      return this.completedItems * 100 / this.totalItems;
    },
    isFinished(): boolean {
      return this.remainingItems === 0;
    },
    derivedReadonly(): boolean {
      return (this.picklist?.Status === ApiPicklistState.Active && !this.picklist.isIncoming) ||
        this.picklist?.Status === ApiPicklistState.Done || (this.picklist?.Status === ApiPicklistState.Todo && this.picklist.Picklistnr !== '-1');
    },
    readonly(): boolean {
      return this.overrideReadonly ?? this.derivedReadonly;
    },
    loading(): boolean {
      return this.canceling || this.submitting || this.parking || this.editing;
    },
    valid(): boolean {
      const pickedData = Object.values(this.picked);
      if (!pickedData.some((p) => p.qty > 0)) return false;

      return !pickedData.some((p) => !p.valid && p.qty > 0);
    },
  },
});
